import { useCallback, useEffect, useRef, useState } from 'react';

export type CopyState = 'idle' | 'copied' | 'failed';

const COPY_FEEDBACK_DURATION_MS = 2000;

export function describeCopyState(copyState: CopyState, idleLabel = 'Copy'): string {
  if (copyState === 'copied') return 'Copied';
  if (copyState === 'failed') return 'Copy failed';
  return idleLabel;
}

export default function useCopyToClipboard() {
  const [copyState, setCopyState] = useState<CopyState>('idle');
  const resetTimerRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (resetTimerRef.current !== null) clearTimeout(resetTimerRef.current);
    },
    [],
  );

  const copy = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyState('copied');
    } catch (e) {
      // Handled here: the caller shows "Copy failed", so the user can select the text manually.
      console.warn('Copying to the clipboard failed; the browser may have denied clipboard permission.', e);
      setCopyState('failed');
    }
    if (resetTimerRef.current !== null) clearTimeout(resetTimerRef.current);
    resetTimerRef.current = window.setTimeout(() => setCopyState('idle'), COPY_FEEDBACK_DURATION_MS);
  }, []);

  return { copyState, copy };
}
